import { Delete as DeleteIcon } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import Disabled from "components/common/Disabled";
import IconButton from "components/common/IconButton";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import StatusReason from "components/common/StatusReason";
import StatusSelect from "components/common/StatusSelectWithChecks";
import PolicyNumberField from "components/layers/PolicyNumberField";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as pricingActions from "store/actions/pricing/pricingActions";
import * as staticDataHooks from "store/hooks/staticDataHooks";
import * as programPricingStore from "store/programPricing";
import * as pricingSelectors from "store/selectors/pricing/pricingSelectors";
import * as staticDataSelectors from "store/selectors/temp/staticData/staticDataSelectors";
import * as userSelectors from "store/selectors/user/userSelectors";

const PerilsCoverage = ({ layerId }) => {
  const dispatch = useDispatch();

  const perilsCoverage = useSelector(programPricingStore.select)?.layers?.[
    layerId
  ]?.perilsCoverage;

  const setPerilsCoverage = (perilsCoverage) => {
    dispatch(
      programPricingStore.updatePathValue({
        path: ["layers", layerId, "perilsCoverage"],
        value: perilsCoverage,
      })
    );
  };

  const { data: catExposuresConfig } = staticDataService.useConfigQuery(
    "cat_exposures"
  );

  const perilsCoverageOptions =
    catExposuresConfig?.enums?.perilsCoverage?.choices ?? [];

  return (
    <Disabled ifReadOnly>
      <Autocomplete
        value={
          perilsCoverageOptions.filter(
            (coverage) => coverage.key === perilsCoverage
          )?.[0] ?? (perilsCoverage == null ? null : { name: "<Unknown>" })
        }
        onChange={(_, value) => setPerilsCoverage(value?.key ?? null)}
        options={perilsCoverageOptions}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={"filled"}
            InputLabelProps={{ shrink: true }}
            size={"small"}
            hiddenLabel
          />
        )}
        autoHighlight
      />
    </Disabled>
  );
};

export const PolicyForm = ({ layer }) => {
  const { data: policyForms } = staticDataService.useConfigQuery(
    "policy_forms"
  );

  const dispatch = useDispatch();

  const policyFormOptions = policyForms?.policy_forms ?? [];

  const policyForm = layer?.policyForm ?? null;

  return (
    <Disabled ifReadOnly>
      <Autocomplete
        value={
          policyFormOptions.filter(
            (option) => option.code === policyForm
          )?.[0] ?? (policyForm == null ? null : { name: "<Unknown>" })
        }
        onChange={(_, value) =>
          dispatch(
            pricingActions.updateLayer({
              layerId: layer?.id,
              values: { policyForm: value?.code ?? null },
            })
          )
        }
        options={policyFormOptions}
        getOptionKey={(option) => option.code}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={"filled"}
            InputLabelProps={{ shrink: true }}
            size={"small"}
            hiddenLabel
          />
        )}
        autoHighlight
      />
    </Disabled>
  );
};

const PaperProvider = ({ layer }) => {
  const dispatch = useDispatch();
  const reinsurersConfig = useSelector(
    staticDataSelectors.selectConfig("reinsurers")
  );

  const paperProvider = layer?.paperProvider ?? null;
  const reinsurers = reinsurersConfig?.reinsurers ?? [];

  if (!reinsurers.length) {
    return <></>;
  }

  return (
    <Disabled ifReadOnly>
      <Autocomplete
        value={
          reinsurers.filter(
            (reinsurer) => reinsurer.key === paperProvider
          )?.[0] ?? (paperProvider == null ? null : { name: "<Unknown>" })
        }
        onChange={(_, value) =>
          dispatch(
            pricingActions.updateLayer({
              layerId: layer?.id,
              values: { paperProvider: value?.key ?? null },
            })
          )
        }
        options={reinsurers}
        getOptionKey={(option) => option.key}
        getOptionLabel={(option) => option.name}
        renderInput={(params) => (
          <TextField
            {...params}
            variant={"filled"}
            InputLabelProps={{ shrink: true }}
            size={"small"}
            hiddenLabel
          />
        )}
        autoHighlight
      />
    </Disabled>
  );
};

const LayerTable = () => {
  const layers = useSelector(pricingSelectors.selectResolvedLayers);
  const dispatch = useDispatch();
  const componentVisibility =
    useSelector(userSelectors.selectUserConfig)?.componentVisibility ?? {};
  const isVisible = useSelector(userSelectors.selectIsVisible);
  const translateLabel = staticDataHooks.useTranslator("uiLabels");

  const columns = [
    {
      key: "deleteLayer",
      title: translateLabel(`LAYERS_TABLE.deleteLayer`, {
        default: "",
      }),
      width: 60,
      align: "left",
      defaultVisibility: true,
      Cell: ({ layerIndex }) => (
        <TableCell align={"left"} sx={{ verticalAlign: "top" }}>
          <Disabled ifReadOnly>
            <IconButton
              icon={DeleteIcon}
              onClick={() => {
                dispatch(pricingActions.deleteLayer(layerIndex));
              }}
            />
          </Disabled>
        </TableCell>
      ),
    },
    {
      key: "status",
      title: translateLabel(`LAYERS_TABLE.status`, {
        default: "",
      }),
      width: 180,
      align: "right",
      defaultVisibility: true,
      Cell: ({ layer }) => (
        <TableCell
          align={"right"}
          sx={{ verticalAlign: "top", paddingTop: "10px" }}
        >
          <Box sx={{ marginTop: "0.5em" }}>
            <Disabled ifReadOnly>
              <StatusSelect
                layer={layer}
                variant={"compact"}
                showQuickSwitch
                status={layer?.status ?? null}
                onChange={(status) => {
                  dispatch(
                    pricingActions.updateLayer({
                      layerId: layer?.id,
                      values: { status, statusReason: null },
                    })
                  );
                }}
              />
            </Disabled>
          </Box>
        </TableCell>
      ),
    },
    {
      key: "statusReason",
      title: translateLabel(`LAYERS_TABLE.statusReason`, {
        default: "Decline/NTU Reason",
      }),
      width: 250,
      defaultVisibility: false,
      Cell: ({ layer }) => (
        <TableCell sx={{ verticalAlign: "middle" }}>
          <Disabled ifReadOnly>
            <StatusReason
              status={layer?.status ?? null}
              reason={layer?.statusReason ?? null}
              onChange={(statusReason) => {
                dispatch(
                  pricingActions.updateLayer({
                    layerId: layer?.id,
                    values: { statusReason },
                  })
                );
              }}
              size={"small"}
              hiddenLabel
            />
          </Disabled>
        </TableCell>
      ),
    },
    {
      key: "limit",
      title: translateLabel(`LAYERS_TABLE.limit`, {
        default: "Limit",
      }),
      width: 250,
      align: "right",
      defaultVisibility: true,
      Cell: ({ layer }) => (
        <TableCell align={"right"} sx={{ verticalAlign: "top" }}>
          <Disabled ifReadOnly>
            <PrettyNumberTextField
              InputLabelProps={{ shrink: true }}
              variant={"filled"}
              size={"small"}
              hiddenLabel
              onChangeNumber={(limit) => {
                dispatch(
                  pricingActions.updateLayer({
                    layerId: layer?.id,
                    values: { limit },
                  })
                );
              }}
              value={layer?.limit ?? null}
              fullWidth
            />
          </Disabled>
        </TableCell>
      ),
    },
    {
      key: "attachment",
      title: translateLabel(`LAYERS_TABLE.attachment`, {
        default: "Attachment",
      }),
      width: 200,
      align: "right",
      defaultVisibility: true,
      Cell: ({ layer }) => (
        <TableCell align={"right"} sx={{ verticalAlign: "top" }}>
          <Disabled ifReadOnly>
            <PrettyNumberTextField
              InputLabelProps={{ shrink: true }}
              variant={"filled"}
              size={"small"}
              hiddenLabel
              onChangeNumber={(attachment) => {
                dispatch(
                  pricingActions.updateLayer({
                    layerId: layer?.id,
                    values: { attachment },
                  })
                );
              }}
              value={layer?.attachment ?? null}
              fullWidth
            />
          </Disabled>
        </TableCell>
      ),
    },
    {
      key: "shareOfLine",
      title: translateLabel(`LAYERS_TABLE.shareOfLine`, {
        default: "Share of Line (%)",
      }),
      width: 200,
      align: "right",
      defaultVisibility: false,
      Cell: ({ layer }) => (
        <TableCell align={"right"} sx={{ verticalAlign: "top" }}>
          <Disabled ifReadOnly>
            <PrettyNumberTextField
              InputLabelProps={{ shrink: true }}
              variant={"filled"}
              size={"small"}
              hiddenLabel
              onChangeNumber={(shareOfLine) => {
                dispatch(
                  pricingActions.updateLayer({
                    layerId: layer?.id,
                    values: { shareOfLine },
                  })
                );
              }}
              value={layer?.shareOfLine ?? null}
              fullWidth
            />
          </Disabled>
        </TableCell>
      ),
    },
    {
      key: "brokerage",
      title: translateLabel(`LAYERS_TABLE.brokerage`, {
        default: "Brokerage (%)",
      }),
      width: 200,
      align: "right",
      defaultVisibility: false,
      Cell: ({ layer }) => (
        <TableCell align={"right"} sx={{ verticalAlign: "top" }}>
          <Disabled ifReadOnly>
            <PrettyNumberTextField
              InputLabelProps={{ shrink: true }}
              variant={"filled"}
              size={"small"}
              hiddenLabel
              onChangeNumber={(brokerage) => {
                dispatch(
                  pricingActions.updateLayer({
                    layerId: layer?.id,
                    values: { brokerage },
                  })
                );
              }}
              value={layer?.brokerage ?? null}
              fullWidth
            />
          </Disabled>
        </TableCell>
      ),
    },
    {
      key: "targetPremium",
      title: translateLabel(`LAYERS_TABLE.targetPremium`, {
        default: "Target Premium",
      }),
      width: 150,
      align: "right",
      defaultVisibility: true,
      Cell: ({ layer }) => (
        <TableCell align={"right"} sx={{ verticalAlign: "top" }}>
          <Disabled ifReadOnly>
            <PrettyNumberTextField
              InputLabelProps={{ shrink: true }}
              variant={"filled"}
              size={"small"}
              hiddenLabel
              onChangeNumber={(brokerTargetGrossPremium) => {
                dispatch(
                  pricingActions.updateLayer({
                    layerId: layer?.id,
                    values: { brokerTargetGrossPremium },
                  })
                );
              }}
              value={layer?.brokerTargetGrossPremium ?? null}
              fullWidth
            />
          </Disabled>
        </TableCell>
      ),
    },
    {
      key: "perils",
      title: translateLabel(`LAYERS_TABLE.perils`, {
        default: "Perils",
      }),
      width: 250,
      align: "right",
      defaultVisibility: true,
      Cell: ({ layer }) => (
        <TableCell align={"right"} sx={{ verticalAlign: "top" }}>
          <PerilsCoverage layerId={layer?.id} />
        </TableCell>
      ),
    },
    {
      key: "scheme",
      title: translateLabel(`LAYERS_TABLE.scheme`, {
        default: "Scheme",
      }),
      width: 250,
      align: "right",
      defaultVisibility: true,
      Cell: ({ layer }) => (
        <TableCell align={"right"} sx={{ verticalAlign: "top" }}>
          <PolicyForm layer={layer} />
        </TableCell>
      ),
    },
    {
      key: "paperProvider",
      title: translateLabel(`LAYERS_TABLE.paperProvider`, {
        default: "Paper Provider",
      }),
      width: 250,
      align: "right",
      defaultVisibility: false,
      Cell: ({ layer }) => (
        <TableCell align={"right"} sx={{ verticalAlign: "top" }}>
          <PaperProvider layer={layer} />
        </TableCell>
      ),
    },
    {
      key: "reference",
      title: translateLabel(`LAYERS_TABLE.reference`, {
        default: "Reference",
      }),
      width: 300,
      align: "right",
      defaultVisibility: true,
      Cell: ({ layer }) => (
        <TableCell align={"right"} sx={{ verticalAlign: "top" }}>
          <PolicyNumberField
            value={layer?.reference ?? ""}
            onChange={(reference) => {
              dispatch(
                pricingActions.updateLayer({
                  layerId: layer?.id,
                  values: { reference },
                })
              );
            }}
            layerId={layer?.id}
            size={"small"}
            hiddenLabel
            label={null}
          />
        </TableCell>
      ),
    },
    {
      key: "spacer",
      title: translateLabel(`LAYERS_TABLE.spacer`, {
        default: "",
      }),
      width: "auto",
      align: "left",
      defaultVisibility: true,
      Cell: () => <TableCell>{/* Fill rest of space */}</TableCell>,
    },
  ].filter((column) => {
    // Special handling for paperProvider - check visibility tag
    if (column.key === "paperProvider") {
      return (
        isVisible("<ActiveLayerEditor>.reinsurance") ??
        componentVisibility?.[`input.coverage.layers.${column.key}`] ??
        column.defaultVisibility ??
        false
      );
    }
    if (column.key === "statusReason") {
      return (
        isVisible("<ActiveLayerEditor>.statusReason") ??
        componentVisibility?.[`input.coverage.layers.${column.key}`] ??
        column.defaultVisibility ??
        false
      );
    }
    return (
      componentVisibility?.[`input.coverage.layers.${column.key}`] ??
      column.defaultVisibility ??
      false
    );
  });

  return (
    <TableContainer>
      <Table size={"small"} className={"condensed-table"}>
        <TableHead>
          <TableRow>
            {columns.map((column) => (
              <TableCell
                key={column.key}
                sx={{ width: column.width }}
                align={column.align}
              >
                {column.title}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {(layers ?? []).map((layer, layerIndex) => (
            <TableRow key={layer?.id ?? layerIndex}>
              {columns.map((column) => (
                <React.Fragment
                  key={`${layer?.id ?? layerIndex}-${column.key}`}
                >
                  {column.Cell({ layer, layerIndex, dispatch })}
                </React.Fragment>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const Layers = () => {
  const dispatch = useDispatch();

  return (
    <Component title={"Layers"}>
      <Stack direction={"column"} spacing={2}>
        <LayerTable />
        <Stack direction={"row"}>
          <Disabled ifReadOnly>
            <Button
              onClick={() => {
                dispatch(pricingActions.addLayer());
              }}
            >
              {"Add Layer"}
            </Button>
          </Disabled>
        </Stack>
      </Stack>
    </Component>
  );
};

export default Layers;
